import { ContactForm } from '@/components/contacts/ContactForm';
import { ContactsEmptyState } from '@/components/contacts/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  useContacts,
  useCreateContact,
  useDeleteContact,
} from '@/hooks/useContacts';
import type { CreateContactDto } from '@/services/ContactService';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

export default function ContactsPage() {
  const [isAddingContact, setIsAddingContact] = useState(false);

  const { data: contacts, isLoading: isLoadingContacts } = useContacts();
  const { mutate: createContact, isPending: isCreating } = useCreateContact();
  const { mutate: deleteContact, isPending: isDeleting } = useDeleteContact();

  const handleAddContact = async (data: CreateContactDto) => {
    createContact(data, {
      onSuccess: () => {
        setIsAddingContact(false);
      },
    });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      deleteContact(id);
    }
  };

  if (isLoadingContacts) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <div className="mb-6 flex items-center justify-end">
        <Sheet open={isAddingContact} onOpenChange={setIsAddingContact}>
          <SheetTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Add New Contact</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <ContactForm onSubmit={handleAddContact} isLoading={isCreating} />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {contacts && contacts.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {contacts.map((contact) => (
            <Card key={contact.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xl font-medium">
                  {contact.first_name} {contact.last_name}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(contact.id)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    <strong>Email:</strong> {contact.email}
                  </p>
                  {contact.phone && (
                    <p className="text-sm text-muted-foreground">
                      <strong>Phone:</strong> {contact.phone}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    <strong>Category:</strong> {contact.category}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex justify-center">
          <ContactsEmptyState onAddContact={() => setIsAddingContact(true)} />
        </div>
      )}
    </div>
  );
}
